<template>
  <div class="card">
    <div class="card-header">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <font-awesome-icon icon="table" class="me-2" />
          Vorlagen
          <span v-if="pagination.totalItems" class="badge bg-secondary ms-2">
            {{ pagination.totalItems }} Vorlage(n)
          </span>
        </h5>
        
        <!-- Table Controls -->
        <div class="d-flex gap-2">
          <!-- Bulk Delete Button -->
          <button 
            v-if="selectedDrafts.length > 0"
            class="btn btn-danger btn-sm"
            @click="deleteSelectedDrafts"
            :disabled="selectedDrafts.length === 0"
          >
            <font-awesome-icon icon="trash" class="me-1" />
            {{ selectedDrafts.length }} löschen
          </button>
          
          <!-- Select All Drafts Checkbox (alle Drafts) -->
          <div class="d-flex align-items-center">
            <div class="form-check d-flex align-items-center">
              <input 
                type="checkbox" 
                class="form-check-input"
                :checked="isAllDraftsSelected"
                :indeterminate="isAllDraftsIndeterminate"
                @change="toggleSelectAllDrafts"
              />
              <label class="form-check-label small ms-1 mb-0">
                Alle Vorlagen
              </label>
            </div>
          </div>
          
          <!-- Table Settings Button -->
          <button 
            class="btn btn-outline-secondary btn-sm" 
            @click="showTableSettingsModal"
          >
            <font-awesome-icon icon="table" />
          </button>
          
          <!-- Advanced Filters -->
          <button 
            class="btn btn-outline-secondary btn-sm position-relative"
            @click="showFieldFilterModal = true"
          >
            <font-awesome-icon icon="filter" class="me-1" />
            Filter
            <span
              v-if="activeFiltersCount > 0"
              class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style="font-size: 0.6rem;"
            >
              {{ activeFiltersCount }}
            </span>
          </button>
          

        </div>
      </div>
    </div>
    
    <!-- Search and Filter -->
    <div v-if="selectedTeamTemplateId" class="card-body">
      <div class="row g-3">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text">
              <font-awesome-icon icon="search" />
            </span>
            <input
              :value="searchQuery"
              @input="handleSearchInput"
              type="text"
              class="form-control"
              placeholder="Nach Vorlagen suchen..."
            />
            <button
              v-if="searchQuery"
              class="btn btn-outline-secondary"
              type="button"
              @click="clearSearch"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No Design Selected -->
    <div v-if="!selectedTeamTemplateId" class="card-body text-center py-5 text-muted">
      <font-awesome-icon icon="list" size="3x" class="mb-3" />
      <h5>Kein Design ausgewählt</h5>
      <p>Wähle ein Design aus dem Dropdown oben aus, um dessen Vorlagen zu verwalten.</p>
    </div>
    
    <!-- Dynamic Table -->
    <div v-else class="card-body p-0">
      <div class="table-responsive" v-if="sortedAndFilteredDrafts.length > 0">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <!-- Checkbox column -->
              <th scope="col" class="text-center align-middle" style="width: 40px;">
                <div class="d-flex justify-content-center">
                  <input 
                    type="checkbox" 
                    class="form-check-input"
                    :checked="isPageAllSelected"
                    :indeterminate="isPageIndeterminate"
                    @change="toggleSelectPage"
                  />
                </div>
              </th>
              
              <!-- Fixed columns -->
              <template v-for="col in fixedColumns" :key="col.key">
                <th 
                  v-if="visibleColumns.includes(col.key)"
                  scope="col" 
                  class="text-nowrap"
                  :class="{ 'sortable': col.sortable }"
                  @click="col.sortable ? sortBy(col.key) : null"
                >
                  <div class="d-flex align-items-center">
                    <font-awesome-icon :icon="col.icon" class="me-1" />
                    {{ col.label }}
                    <font-awesome-icon 
                      v-if="col.sortable" 
                      :icon="getSortIcon(col.key)" 
                      class="ms-1 sort-icon"
                    />
                  </div>
                </th>
              </template>
              
              <!-- Dynamic columns based on template fields -->
              <template v-for="field in dynamicTableFields" :key="field.name">
                <th 
                  v-if="visibleColumns.includes(field.name)"
                  scope="col" 
                  class="text-nowrap sortable"
                  @click="sortBy(field.name)"
                >
                  <div class="d-flex align-items-center">
                    <font-awesome-icon :icon="getFieldIcon(field.type)" class="me-1" />
                    {{ field.displayName }}
                    <font-awesome-icon 
                      :icon="getSortIcon(field.name)" 
                      class="ms-1 sort-icon"
                    />
                  </div>
                </th>
              </template>
              
              <!-- Actions column -->
              <th scope="col" class="text-nowrap text-end">
                <font-awesome-icon icon="cog" class="me-1" />
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="draft in sortedAndFilteredDrafts" :key="draft.id">
              <!-- Checkbox column -->
              <td class="text-center align-middle">
                <div class="d-flex justify-content-center">
                  <input 
                    type="checkbox" 
                    class="form-check-input"
                    :checked="selectedDrafts.includes(draft.id)"
                    @change="toggleDraftSelection(draft.id)"
                  />
                </div>
              </td>
              
                            <!-- Fixed columns -->
              <template v-for="col in fixedColumns" :key="col.key + draft.id">
                <td 
                  v-show="visibleColumns.includes(col?.key)"
                  class="text-nowrap" >
                    <!-- Type Column -->
                    <div v-if="col.key === 'type'">
                      <span :class="`badge ${getTypeBadgeClass(draft.data?.type)}`">
                        {{ getTypeLabel(draft.type) }}
                      </span>
                    </div>
                </td>
              </template>
              
              <!-- Dynamic columns based on template fields -->
              <template v-for="field in dynamicTableFields" :key="field.name">
                <td 
                  v-if="visibleColumns.includes(field.name)"
                  class="text-nowrap"
                >
                  <span v-if="getDraftDataFields(draft)[field.name]">
                    <div v-if="field.type === 'money' && field.isCalculated && field.options?.pricePerUnit">
                      <div class="d-flex flex-column">
                        <div class="d-flex align-items-center gap-1">
                          <span class="fw-semibold">{{ formatFieldValue(getCalculatedValue(draft, field), field) }}</span>
                          <font-awesome-icon icon="calculator" class="text-info" size="sm" title="Berechnetes Feld" />
                        </div>
                        <small class="text-muted">
                          {{ field.options.pricePerUnit }}€/{{ field.options.unit || 'Einheit' }}
                          <span v-if="field.options.dependsOn && getDraftDataFields(draft)[field.options.dependsOn]">
                            ({{ getDraftDataFields(draft)[field.options.dependsOn].data }} {{ field.options.unit || 'Einheiten' }})
                          </span>
                        </small>
                      </div>
                    </div>
                    <span v-else>
                      {{ formatFieldValue(getDraftDataFields(draft)[field.name].data, field) }}
                    </span>
                  </span>
                  <span v-else class="text-muted">
                    <small>-</small>
                  </span>
                </td>
              </template>
              
              <!-- Actions column -->
              <td class="text-nowrap text-end">
                <div class="btn-group btn-group-sm">
                  <button 
                    v-if="draft.data?.type === 'api'"
                    class="btn btn-outline-primary"
                    @click="$emit('execute-api', draft)"
                    :disabled="draft.data?.status === 'processing'"
                    title="API ausführen"
                  >
                    <font-awesome-icon icon="play" />
                  </button>
                  <button 
                    v-else
                    class="btn btn-outline-primary"
                    @click="$emit('edit-draft', draft)"
                    title="Bearbeiten"
                  >
                    <font-awesome-icon icon="edit" />
                  </button>
                  <button 
                    class="btn btn-outline-danger"
                    @click="$emit('delete-draft', draft)"
                    title="Löschen"
                  >
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Empty State -->
      <div v-if="sortedAndFilteredDrafts.length === 0" class="text-center py-5">
        <font-awesome-icon icon="file-alt" size="3x" class="text-muted mb-3" />
        <h5 class="text-muted">Keine Vorlagen gefunden</h5>
        <p class="text-muted">
          {{ drafts.length === 0 ? 'Erstelle deine erste Vorlage.' : 'Keine Vorlagen entsprechen den aktuellen Filtern.' }}
        </p>
        <div class="d-flex gap-2 justify-content-center">
          <button v-if="drafts.length === 0 && props.activeFiltersCount === 0" class="btn btn-primary" @click="$emit('create-draft')">
            <font-awesome-icon icon="plus" class="me-2" />
            Erste Vorlage erstellen
          </button>
          <button v-else-if="props.activeFiltersCount > 0" class="btn btn-outline-secondary" @click="$emit('clear-filters')">
            <font-awesome-icon icon="times" class="me-2" />
            Filter löschen
          </button>
        </div>
      </div>
    </div>
    
    <!-- Pagination -->
    <div v-if="selectedTeamTemplateId && pagination.totalPages > 1" class="card-footer">
      <nav aria-label="Vorlagen Paginierung">
        <ul class="pagination justify-content-center mb-0">
          <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
            <button class="page-link" @click="$emit('change-page', pagination.currentPage - 1)">
              <font-awesome-icon icon="chevron-left" />
            </button>
          </li>
          
          <li 
            v-for="page in getVisiblePages()" 
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.currentPage }"
          >
            <button class="page-link" @click="$emit('change-page', page)">
              {{ page }}
            </button>
          </li>
          
          <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
            <button class="page-link" @click="$emit('change-page', pagination.currentPage + 1)">
              <font-awesome-icon icon="chevron-right" />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <!-- Field Filter Modal -->
  <Teleport to="#modalTarget">
    <div v-if="showFieldFilterModal" class="modal show d-block filter-modal">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="filter" class="me-2" />
            Erweiterte Filter
          </h5>
          <button type="button" class="btn-close" @click="showFieldFilterModal = false"></button>
        </div>
        <div class="modal-body">
          <!-- Loading Overlay for Auto-Reload -->
          <div v-if="filterModalLoading" class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center filter-loading-overlay" style="top: 0; left: 0; z-index: 10;">
            <div class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Lade...</span>
              </div>
              <p class="mt-2 text-muted">Filter werden angewendet...</p>
            </div>
          </div>

          <!-- Type Filter -->
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Typ</label>
              <select v-model="filters.type" class="form-select">
                <option value="">Alle Typen</option>
                <option value="manual">Manuell</option>
                <option value="excel">Excel</option>
                <option value="api">API</option>
              </select>
            </div>
          </div>

          <!-- Datum Filter -->
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Datum von</label>
              <input
                v-model="filters.dateFrom"
                type="date"
                class="form-control"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Datum bis</label>
              <input
                v-model="filters.dateTo"
                type="date"
                class="form-control"
              />
            </div>
          </div>

          <!-- Verfügbare Feld-Filter -->
          <div v-if="Object.keys(availableFilters.fieldValues).length > 0" class="mb-4">
            <label class="form-label fw-bold">Feld-Filter</label>
            <div class="row g-3">
              <div
                v-for="(fieldInfo, fieldName) in availableFilters.fieldValues"
                :key="fieldName"
                class="col-md-6"
              >
                <div class="form-group">
                  <label class="form-label small fw-semibold">
                    {{ fieldInfo.displayName }}
                    <span class="text-muted">({{ fieldInfo.values ? fieldInfo.values.length : 0 }} Optionen)</span>
                  </label>
                  
                  <!-- Multi-Select für Feld-Werte -->
                  <div class="multiselect-container">
                    <div class="multiselect-input" @click="toggleMultiselect(fieldName)">
                      <div class="multiselect-selected">
                        <span v-if="!filters.fields[fieldName] || filters.fields[fieldName].length === 0" class="text-muted">
                          Alle auswählen...
                        </span>
                        <span v-else class="selected-count">
                          {{ filters.fields[fieldName].length }} ausgewählt
                        </span>
                      </div>
                      <font-awesome-icon icon="chevron-down" class="multiselect-arrow" />
                    </div>
                    
                    <!-- Dropdown -->
                    <div v-if="openMultiselect === fieldName" class="multiselect-dropdown">
                      <!-- Search -->
                      <div class="multiselect-search">
                        <input
                          v-model="multiselectSearch[fieldName]"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Suchen..."
                          @click.stop
                        />
                      </div>
                      
                      <!-- Options -->
                      <div class="multiselect-options">
                        <div
                          v-for="option in getFilteredOptions(fieldName)"
                          :key="option.value"
                          class="multiselect-option"
                          @click="toggleOption(fieldName, option.value)"
                        >
                          <div class="form-check">
                            <input
                              :checked="isOptionSelected(fieldName, option.value)"
                              type="checkbox"
                              class="form-check-input"
                              @click.stop
                            />
                            <label class="form-check-label">
                              {{ option.label }}
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Actions -->
                      <div class="multiselect-actions">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          @click="clearFieldOptions(fieldName)"
                        >
                          löschen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Filters Summary -->
          <div v-if="activeFiltersCount > 0" class="mb-3">
            <label class="form-label fw-bold">Aktive Filter ({{ activeFiltersCount }})</label>
            
            <!-- Type Filter -->
            <div v-if="filters.type" class="mb-2">
              <small class="text-muted fw-semibold">Typ-Filter:</small>
              <div class="d-flex flex-wrap gap-2 mt-1">
                <span class="badge bg-info">
                  Typ: {{ getTypeLabel(filters.type) }}
                  <button
                    type="button"
                    class="btn-close btn-close-white ms-1"
                    style="font-size: 0.5rem;"
                    @click="filters.type = ''"
                  ></button>
                </span>
              </div>
            </div>
            
            <!-- Date Filters -->
            <div v-if="filters.dateFrom || filters.dateTo" class="mb-2">
              <small class="text-muted fw-semibold">Datums-Filter:</small>
              <div class="d-flex flex-wrap gap-2 mt-1">
                <span
                  v-if="filters.dateFrom"
                  class="badge bg-info"
                >
                  Von: {{ filters.dateFrom }}
                  <button
                    type="button"
                    class="btn-close btn-close-white ms-1"
                    style="font-size: 0.5rem;"
                    @click="filters.dateFrom = ''"
                  ></button>
                </span>
                
                <span
                  v-if="filters.dateTo"
                  class="badge bg-info"
                >
                  Bis: {{ filters.dateTo }}
                  <button
                    type="button"
                    class="btn-close btn-close-white ms-1"
                    style="font-size: 0.5rem;"
                    @click="filters.dateTo = ''"
                  ></button>
                </span>
              </div>
            </div>
     
            <!-- Field Filters: flach, nicht nach Kategorie gruppiert -->
            <div class="d-flex flex-wrap gap-1 mt-1">
              <span
                v-for="entry in activeFieldFilterList"
                :key="entry.fieldName + ':' + entry.value"
                class="badge bg-primary"
                style="font-size: 0.75rem;"
              >
                {{ getFieldDisplayName(entry.fieldName) }}: {{ entry.label }}
                <button
                  type="button"
                  class="btn-close btn-close-white ms-1"
                  style="font-size: 0.5rem;"
                  @click="removeOption(entry.fieldName, entry.value)"
                ></button>
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="clearAllFilters">
            Alle Filter löschen
          </button>
          <button type="button" class="btn btn-primary" @click="showFieldFilterModal = false">
            Schließen
          </button>
        </div>
      </div>
    </div>
    </div>

    <!-- Modal Backdrop -->
    <div v-if="showFieldFilterModal" class="modal-backdrop fade show filter-modal-backdrop"></div>
  </Teleport>

  <!-- Table Settings Modal -->
  <Teleport to="#modalTarget">
    <div 
      v-if="showTableModal" 
      class="modal show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0, 0, 0, 0.5);"
    >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="table" class="me-2" />
            Tabellen-Einstellungen
          </h5>
          <button type="button" class="btn-close" @click="closeTableModal"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-md-6">
              <h6 class="mb-3">Standard-Spalten:</h6>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="table-col-type" 
                  v-model="visibleColumns" 
                  value="type"
                >
                <label class="form-check-label" for="table-col-type">Typ</label>
              </div>
            </div>
            <div class="col-md-6">
              <h6 class="mb-3">Design-Feld-Spalten:</h6>
              <div v-for="field in dynamicTableFields" :key="field.name" class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :id="`table-col-${field.name}`" 
                  v-model="visibleColumns" 
                  :value="field.name"
                >
                <label class="form-check-label" :for="`table-col-${field.name}`">{{ field.displayName || field.name }}</label>
              </div>
            </div>
          </div>
          <div class="mt-4">
            <div class="d-flex gap-2">
              <button 
                class="btn btn-outline-primary" 
                @click="selectAllTableColumns"
              >
                <font-awesome-icon icon="check-square" class="me-2" />
                Alle Spalten anzeigen
              </button>
              <button 
                class="btn btn-outline-secondary" 
                @click="resetTableColumns"
              >
                <font-awesome-icon icon="square" class="me-2" />
                Standard-Spalten
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeTableModal">
            Schließen
          </button>
          <button type="button" class="btn btn-primary" @click="closeTableModal">
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  </div>  </Teleport>

</template>

<script setup>
import { computed, ref, watch, onMounted , onUnmounted } from 'vue'

const props = defineProps({
  drafts: {
    type: Array,
    default: () => []
  },
  teamTemplateFields: {
    type: Array,
    default: () => []
  },
  selectedTeamTemplateId: {
    type: String,
    default: null
  },
  pagination: {
    type: Object,
    default: () => ({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10
    })
  },
  filters: {
    type: Object,
    default: () => ({
      type: '',
      dateFrom: '',
      dateTo: '',
      fields: {}
    })
  },
  sortColumn: {
    type: String,
    default: 'createdAt'
  },
  sortDirection: {
    type: String,
    default: 'desc'
  },
  activeFiltersCount: {
    type: Number,
    default: 0
  },
  searchQuery: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'execute-api',
  'edit-draft', 
  'delete-draft',
  'create-draft',
  'change-page',
  'sort-change',
  'filter-change',
  'clear-filters',
  'delete-multiple-drafts',
  'select-all-drafts',
  'column-visibility-change',
  'search-change'
])


// Reactive variables
const visibleColumns = ref(['type'])
const selectedDrafts = ref([])
const showFieldFilterModal = ref(false)
const filterModalLoading = ref(false)
const showTableModal = ref(false)
let searchTimeout = null

watch(() => props.teamTemplateFields, (newFields) => {
  visibleColumns.value = ['type']
  
  if (newFields.length > 0) {
    newFields.forEach(field => {
      if (field.type !== 'file' && !visibleColumns.value.includes(field.name)) {
        visibleColumns.value.push(field.name)
      }
    })
  }
}, { immediate: true })


const filters = ref({
  type: '',
  dateFrom: '',
  dateTo: '',
  fields: {}
})


const availableFilters = ref({
  fieldValues: {}
})


const openMultiselect = ref(null)
const multiselectSearch = ref({})


const fixedColumns = [
  {
    key: 'type',
    label: 'Typ',
    icon: 'info-circle',
    sortable: true
  }
]


const dynamicTableFields = computed(() => {
  return props.teamTemplateFields.filter(field => field.type !== 'file')
})


const getDraftDataFields = (draft) => {
  if (!draft.data || typeof draft.data !== 'object') return {}
  
  const fields = {}
  Object.entries(draft.data).forEach(([key, value]) => {
    if (value && typeof value === 'object' && value.label && value.data !== undefined) {
      fields[key] = {
        label: value.label,
        data: value.data,
        category: value.category
      }
    }
  })
  return fields
}


const sortedAndFilteredDrafts = computed(() => {
  
  
  return props.drafts
})



const isPageAllSelected = computed(() => {
  return props.drafts.length > 0 && 
         props.drafts.every(draft => selectedDrafts.value.includes(draft.id))
})

const isPageIndeterminate = computed(() => {
  const selectedOnPage = props.drafts.filter(draft => selectedDrafts.value.includes(draft.id)).length
  return selectedOnPage > 0 && selectedOnPage < props.drafts.length
})


const isAllDraftsSelected = computed(() => {
  
  
  return props.drafts.length > 0 && 
         selectedDrafts.value.length >= props.drafts.length &&
         props.drafts.every(draft => selectedDrafts.value.includes(draft.id))
})

const isAllDraftsIndeterminate = computed(() => {
  return selectedDrafts.value.length > 0 && 
         !props.drafts.every(draft => selectedDrafts.value.includes(draft.id))
})


const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.type) count++
  if (filters.value.dateFrom) count++
  if (filters.value.dateTo) count++
  count += Object.values(filters.value.fields).reduce((acc, v) => {
    if (v && Array.isArray(v)) return acc + v.length
    return acc
  }, 0)
  return count
})


const activeFieldFilterList = computed(() => {
  const list = []
  const fields = filters.value?.fields || {}
  Object.entries(fields).forEach(([fieldName, selectedValues]) => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return
    selectedValues.forEach((singleValue) => {
      list.push({
        fieldName,
        value: singleValue,
        label: getOptionLabel(fieldName, singleValue)
      })
    })
  })
  return list
})


watch(() => props.teamTemplateFields, (newFields) => {
  if (newFields.length > 0) {
    
    newFields.forEach(field => {
      if (field.type !== 'file' && !visibleColumns.value.includes(field.name)) {
        visibleColumns.value.push(field.name)
      }
    })
  }
}, { immediate: true })


const toggleColumn = (columnKey) => {
  const index = visibleColumns.value.indexOf(columnKey)
  if (index > -1) {
    visibleColumns.value.splice(index, 1)
  } else {
    visibleColumns.value.push(columnKey)
  }
  
  
  emit('column-visibility-change', visibleColumns.value)
}

const sortBy = (column) => {
  let newDirection = 'asc'
  if (props.sortColumn === column) {
    newDirection = props.sortDirection === 'asc' ? 'desc' : 'asc'
  }
  emit('sort-change', { column, direction: newDirection })
}

const getSortIcon = (column) => {
  if (props.sortColumn !== column) return 'sort'
  return props.sortDirection === 'asc' ? 'sort-up' : 'sort-down'
}

const applyFilters = () => {
  emit('filter-change', props.filters)
}

const clearFilters = () => {
  emit('clear-filters')
}


const toggleDraftSelection = (draftId) => {
  const index = selectedDrafts.value.indexOf(draftId)
  if (index > -1) {
    selectedDrafts.value.splice(index, 1)
  } else {
    selectedDrafts.value.push(draftId)
  }
}


const toggleSelectPage = () => {
  if (isPageAllSelected.value) {
    
    props.drafts.forEach(draft => {
      const index = selectedDrafts.value.indexOf(draft.id)
      if (index > -1) {
        selectedDrafts.value.splice(index, 1)
      }
    })
  } else {
    
    props.drafts.forEach(draft => {
      if (!selectedDrafts.value.includes(draft.id)) {
        selectedDrafts.value.push(draft.id)
      }
    })
  }
}


const toggleSelectAllDrafts = () => {
  if (isAllDraftsSelected.value) {
    
    selectedDrafts.value = []
  } else {
    
    emit('select-all-drafts')
  }
}

const deleteSelectedDrafts = () => {
  if (selectedDrafts.value.length > 0) {
    emit('delete-multiple-drafts', selectedDrafts.value)
  }
}


const setAllDraftIds = (allDraftIds) => {
  selectedDrafts.value = allDraftIds
}


defineExpose({
  setAllDraftIds
})






const getFieldIcon = (fieldType) => {
  const icons = {
    text: 'font',
    number: 'hashtag',
    date: 'calendar',
    select: 'list',
    checkbox: 'check-square',
    textarea: 'align-left',
    money: 'euro-sign',
    location: 'map-marker-alt'
  }
  return icons[fieldType] || 'tag'
}

const formatFieldValue = (value, field) => {
  if (!value) return '-'
  
  
  switch (field.type) {
    case 'date':
      return new Date(value).toLocaleDateString('de-DE')
    case 'checkbox':
      return value ? 'Ja' : 'Nein'
    case 'number':
      return typeof value === 'number' ? value.toLocaleString('de-DE') : value
    case 'money':
      if (typeof value === 'number') {
        
        return value.toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
      return value
    case 'select':
      
      if (field.options?.choices) {
        const choice = field.options.choices.find(c => 
          (c.value || c) === value
        )
        return choice ? (choice.label || choice) : value
      }
      return value
    default:
      return value
  }
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const getTypeBadgeClass = (type) => {
  const classes = {
    api: 'bg-primary',
    excel: 'bg-success',
    manual: 'bg-secondary'
  }
  return classes[type] || 'bg-secondary'
}

const getTypeLabel = (type) => {
  const labels = {
    api: 'API',
    excel: 'Excel',
    manual: 'Manuell'
  }
  return labels[type] || type
}

const getVisiblePages = () => {
  const pages = []
  const current = props.pagination.currentPage
  const total = props.pagination.totalPages
  
  
  pages.push(1)
  
  
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    if (i > 1 && i < total) {
      pages.push(i)
    }
  }
  
  
  if (total > 1) {
    pages.push(total)
  }
  
  return [...new Set(pages)].sort((a, b) => a - b)
}


const toggleMultiselect = (fieldName) => {
  if (openMultiselect.value === fieldName) {
    openMultiselect.value = null
  } else {
    openMultiselect.value = fieldName
    if (!multiselectSearch.value[fieldName]) {
      multiselectSearch.value[fieldName] = ''
    }
  }
}

const getFilteredOptions = (fieldName) => {
  const options = getFieldFilterOptions(fieldName)
  const searchTerm = multiselectSearch.value[fieldName] || ''
  
  if (!searchTerm) return options
  
  return options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  )
}

const toggleOption = (fieldName, value) => {
  if (!filters.value.fields[fieldName]) {
    filters.value.fields[fieldName] = []
  }
  
  const index = filters.value.fields[fieldName].indexOf(value)
  if (index > -1) {
    filters.value.fields[fieldName].splice(index, 1)
  } else {
    filters.value.fields[fieldName].push(value)
  }
}

const isOptionSelected = (fieldName, value) => {
  return filters.value.fields[fieldName] && 
         filters.value.fields[fieldName].includes(value)
}

const clearFieldOptions = (fieldName) => {
  filters.value.fields[fieldName] = []
  multiselectSearch.value[fieldName] = ''
}

const getOptionLabel = (fieldName, value) => {
  const fieldInfo = availableFilters.value.fieldValues[fieldName]
  if (fieldInfo && fieldInfo.type) {
    const type = fieldInfo.type
    if (type === 'money' || type === 'number') {
      return formatAmount ? formatAmount(value) : value
    }
    if (type === 'date') {
      return formatDate(value)
    }
    if (type === 'checkbox') {
      return value === true || value === 'true' ? 'Ja' : 'Nein'
    }
  }
  return String(value)
}

const removeOption = (fieldName, value) => {
  if (filters.value.fields[fieldName]) {
    const index = filters.value.fields[fieldName].indexOf(value)
    if (index > -1) {
      filters.value.fields[fieldName].splice(index, 1)
    }
  }
}

const getFieldFilterOptions = (fieldName) => {
  const fieldInfo = availableFilters.value.fieldValues[fieldName]
  if (!fieldInfo || !fieldInfo.values) return []
  
  return fieldInfo.values.map(value => ({
    value: value,
    label: value,
    type: fieldInfo.type,
    displayName: fieldInfo.displayName
  }))
}

const getFieldDisplayName = (fieldName) => {
  const fieldInfo = availableFilters.value.fieldValues[fieldName]
  return fieldInfo ? fieldInfo.displayName : fieldName
}

const getCalculatedValue = (draft, field) => {
  if (!field.isCalculated || field.type !== 'money' || !field.options?.pricePerUnit) {
    return getDraftDataFields(draft)[field.name]?.data
  }
  
  
  const draftFields = getDraftDataFields(draft)
  const quantityField = field.options.dependsOn ? draftFields[field.options.dependsOn] : null
  const quantity = quantityField?.data || 0
  
  
  const calculatedValue = parseFloat(quantity) * parseFloat(field.options.pricePerUnit)
  
  
  return isNaN(calculatedValue) ? getDraftDataFields(draft)[field.name]?.data : calculatedValue
}

const clearAllFilters = () => {
  filters.value = {
    type: '',
    dateFrom: '',
    dateTo: '',
    fields: {}
  }
  multiselectSearch.value = {}
  openMultiselect.value = null
}

const showTableSettingsModal = () => {
  showTableModal.value = true
}

const closeTableModal = () => {
  showTableModal.value = false
}

const selectAllTableColumns = () => {
  const allColumns = ['type']
  if (props.teamTemplateFields) {
    props.teamTemplateFields.forEach(field => {
      if (field.type !== 'file') {
        allColumns.push(field.name)
      }
    })
  }
  visibleColumns.value = allColumns
}

const resetTableColumns = () => {
  visibleColumns.value = ['type']
}

const handleSearchInput = (event) => {
  const query = event.target.value
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    emit('search-change', query)
  }, 300)
}

const clearSearch = () => {
  emit('search-change', '')
}


const handleClickOutside = (event) => {
  if (openMultiselect.value && !event.target.closest('.multiselect-container')) {
    openMultiselect.value = null
  }
}


watch(() => [filters.value.type, filters.value.dateFrom, filters.value.dateTo], async () => {
  if (!props.selectedTeamTemplateId) return
  const shouldShowOverlay = showFieldFilterModal.value
  if (shouldShowOverlay) filterModalLoading.value = true
  try {
    emit('filter-change', filters.value)
  } finally {
    if (shouldShowOverlay) filterModalLoading.value = false
  }
})

watch(() => filters.value.fields, async () => {
  if (!props.selectedTeamTemplateId) return
  const shouldShowOverlay = showFieldFilterModal.value
  if (shouldShowOverlay) filterModalLoading.value = true
  try {
    emit('filter-change', filters.value)
  } finally {
    if (shouldShowOverlay) filterModalLoading.value = false
  }
}, { deep: true })


onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.filter-modal {
  z-index: 2000;
}

.filter-modal-backdrop {
  z-index: 1990;
}
</style>
